import React, { useEffect, useState } from 'react';
import { Bell, ExternalLink, RefreshCw } from 'lucide-react';
import axios from 'axios';
import { useLanguage } from '../../context/LanguageContext';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000';

const FALLBACK_NOTIFICATIONS = [
  {
    id: 1,
    title: 'PMK No. 71 Tahun 2026: Pembaruan prosedur keberatan, urutan alokasi pembayaran pokok, dan pengurangan sanksi Pasal 27A',
    time: 'Okt 2026',
    urgent: true,
    source: 'kemenkeu.go.id',
    url: 'https://jdih.kemenkeu.go.id',
  },
  {
    id: 2,
    title: 'PER-12/PJ/2026: Ketentuan pembetulan SPT Masa PPN skema replace & kompensasi lebih bayar di sistem Coretax DJP',
    time: 'Okt 2026',
    urgent: true,
    source: 'pajak.go.id',
    url: 'https://www.pajak.go.id',
  },
  {
    id: 3,
    title: 'PMK No. 44 Tahun 2026: Persyaratan terbaru Surat Kuasa Khusus perpajakan (masa berlaku & meterai resmi)',
    time: 'Jul 2026',
    urgent: false,
    source: 'jdih.kemenkeu.go.id',
    url: 'https://jdih.kemenkeu.go.id',
  },
  {
    id: 4,
    title: 'Tarif efektif PPh 21 (TER) PP 58/2023 & PMK 168/2023 — pastikan rekonsiliasi masa pajak akhir tahun sesuai',
    time: 'Regulasi Aktif',
    urgent: false,
    source: 'pajak.go.id',
    url: 'https://www.pajak.go.id',
  },
];

function deduplicateItems(items) {
  const seen = new Set();
  const result = [];
  for (const item of items) {
    const normTitle = (item.title || '').trim().toLowerCase();
    if (!normTitle || seen.has(normTitle)) continue;
    seen.add(normTitle);
    result.push(item);
  }
  return result;
}

export default function NotificationPanel() {
  const { isEnglish } = useLanguage();
  const [notifications, setNotifications] = useState(FALLBACK_NOTIFICATIONS);
  const [loading, setLoading] = useState(false);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_BASE}/api/info-penting?limit=8`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        timeout: 10000,
      });

      if (res.data?.status === 'ok' && res.data?.data?.length > 0) {
        const mapped = res.data.data.map((item, idx) => ({
          id: item.id || idx + 1,
          title: item.title,
          time: item.published_date || '',
          urgent: item.is_urgent || false,
          source: item.source || 'pajak.go.id',
          url: item.url || '',
        }));
        const unique = deduplicateItems(mapped).slice(0, 4);
        if (unique.length > 0) {
          setNotifications(unique);
        }
      }
    } catch (err) {
      // Fallback
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="gold-card p-5">
      <div className="flex items-center justify-between mb-3.5">
        <div className="flex items-center gap-2">
          <Bell size={13} className="text-text-sec" />
          <span className="text-[11px] uppercase font-medium tracking-wider text-text-sec">
            {isEnglish ? 'Tax Regulation Updates' : 'Info Regulasi Pajak'}
          </span>
        </div>
        <button
          onClick={fetchNotifications}
          disabled={loading}
          className="p-1 rounded-md text-text-sec hover:text-text-pri hover:bg-surface-hover transition-colors"
          title="Refresh"
        >
          <RefreshCw size={11} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      <div className="divide-y divide-ledger-border/60">
        {notifications.map((item) => (
          <a
            key={item.id}
            href={item.url || '#'}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-start justify-between gap-2.5 group py-3 first:pt-0 last:pb-0 transition-colors"
          >
            <div className="min-w-0 flex-1">
              <p className="text-xs text-text-pri group-hover:underline leading-relaxed font-normal line-clamp-2">
                {item.title}
              </p>
              <div className="flex items-center gap-1.5 mt-1 text-[10.5px] text-text-sec">
                <span>{item.source}</span>
                {item.time && (
                  <>
                    <span>·</span>
                    <span className="font-mono">{item.time}</span>
                  </>
                )}
              </div>
            </div>
            {item.url && (
              <ExternalLink
                size={11}
                className="text-muted group-hover:text-text-pri mt-1 shrink-0 transition-colors"
              />
            )}
          </a>
        ))}
      </div>
    </div>
  );
}
